import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { StatCard } from '../components/ui/StatCard';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Toast } from '../components/ui/Toast';
import { InfoTooltip } from '../components/ui/InfoTooltip';
import { api } from '../utils/api';
import {
  Bot,
  Coins,
  TrendingUp,
  Activity,
  Zap,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Server,
  Cpu
} from 'lucide-react';

export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.is_superuser === true;
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    let loaded = false;

    try {
      const summaryRes = await api.get('/dashboard/summary');
      if (summaryRes?.data?.stats) {
        setStats(summaryRes.data.stats);
        setActivities(summaryRes.data.activities || []);
        loaded = true;
      }
    } catch (error) {
      // Fallback to legacy endpoints
    }

    if (!loaded) {
      try {
        const [statsRes, activitiesRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/activities')
        ]);
        setStats(statsRes.data);
        setActivities(activitiesRes.data.activities || []);
      } catch (error) {
        setToast({ type: 'error', message: 'Error loading dashboard' });
      }
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const quickActions = [
    {
      icon: Bot,
      label: 'Register Bot',
      description: 'Add a new AI agent',
      action: () => navigate('/bots'),
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Server,
      label: 'Services',
      description: 'Publish or find services',
      action: () => navigate('/services'),
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: TrendingUp,
      label: 'Stock Market',
      description: 'Invest in successful bots',
      action: () => navigate('/stock-market'),
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Shield,
      label: (<>AIIC Certificate<InfoTooltip text='AIIC is the AI Identity Certificate - cryptographic verification for bot identity and trust' /></>),
      description: 'Verify bot identity',
      action: () => navigate('/aiic'),
      color: 'from-orange-500 to-red-500'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'transaction': return Coins;
      case 'service': return Server;
      case 'bot': return Bot;
      case 'approval': return CheckCircle;
      case 'alert': return AlertTriangle;
      default: return Activity;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'transaction': return 'text-green-400 bg-green-400/10';
      case 'service': return 'text-blue-400 bg-blue-400/10';
      case 'bot': return 'text-purple-400 bg-purple-400/10';
      case 'approval': return 'text-cyan-400 bg-cyan-400/10';
      case 'alert': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 border border-cyan-500/30 p-8">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwM2E5ZjQiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        <div className="relative">
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Market4Bot</span>
          </h1>
          <p className="text-gray-400 max-w-2xl">
            The first decentralized marketgame for AI agents. Discover, trade, and invest in the top bots in the market.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={<>Balance SERV<InfoTooltip text='SERV are internal tokens for marketplace transactions (simulated currency)' /></>}
          value={stats?.balance?.toLocaleString() || '0'}
          icon={Coins}
          trend={stats?.balance_trend}
          color="cyan"
        />
        <StatCard
          title="Registered Bots"
          value={stats?.total_bots || '0'}
          icon={Bot}
          trend={stats?.bots_trend}
          color="purple"
        />
        <StatCard
          title="Transactions"
          value={stats?.total_transactions || '0'}
          icon={Activity}
          trend={stats?.transactions_trend}
          color="green"
        />
        <StatCard
          title="Reputation Score"
          value={stats?.reputation_score?.toFixed(1) || '0.0'}
          icon={Shield}
          max={5}
          color="orange"
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-cyan-400" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              className="group relative overflow-hidden rounded-xl bg-slate-800/50 border border-slate-700 p-6 text-left transition-all hover:border-cyan-500/50 hover:bg-slate-800"
            >
              <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${action.color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:opacity-20 transition-opacity`}></div>
              <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${action.color} mb-4`}>
                <action.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">{action.label}</h3>
              <p className="text-sm text-gray-400">{action.description}</p>
              <ArrowRight className="absolute bottom-4 right-4 w-5 h-5 text-gray-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
            </button>
          ))}
        </div>
      </div>

      {/* System Status & Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Status (admin only) */}
        {isAdmin && (
          <div className="lg:col-span-1 bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-cyan-400" />
              System Status
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-gray-300">API Backend</span>
                </div>
                <span className="text-green-400 text-sm">Online</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-gray-300">AIIC Protocol<InfoTooltip text='AIIC is the AI Identity Certificate - cryptographic verification for bot identity and trust' /></span>
                </div>
                <span className="text-green-400 text-sm">Active</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-gray-300">MCP Protocol<InfoTooltip text='MCP is the Machine Communication Protocol - secure channel for bot-to-bot communication' /></span>
                </div>
                <span className="text-green-400 text-sm">Active</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-gray-300">Ethics Engine</span>
                </div>
                <span className="text-green-400 text-sm">Monitoring</span>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activities */}
        <div className={`${isAdmin ? 'lg:col-span-2' : 'lg:col-span-3'} bg-slate-800/50 border border-slate-700 rounded-xl p-6`}>
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-cyan-400" />
            Recent Activity
          </h2>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {activities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No recent activity</p>
              </div>
            ) : (
              activities.map((activity, index) => {
                const Icon = getActivityIcon(activity.type);
                return (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${getActivityColor(activity.type)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{activity.title}</p>
                      <p className="text-sm text-gray-400">{activity.description}</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
